import { Link } from 'react-router-dom';

const projects = [
  {
    id: 1,
    label: 'Refinishing — Columbia, MD',
    service: 'Hardwood Refinishing',
    serviceSlug: 'refinishing',
    before: 'https://public.readdy.ai/ai/img_res/5d10b2d96ac7031145073f8afe2c9704.jpg',
    after: 'https://readdy.ai/api/search-image?query=stunning%20freshly%20refinished%20gleaming%20hardwood%20floors%20rich%20dark%20walnut%20stain%20flawless%20finish%20beautiful%20wood%20grain%20elegant%20Maryland%20home%20natural%20light&width=700&height=480&seq=after-1&orientation=landscape',
    result: 'Floors fully restored — 70% cost savings vs. replacement',
  },
  {
    id: 2,
    label: 'New Installation — Annapolis, MD',
    service: 'Installation',
    serviceSlug: 'installation',
    before: 'https://readdy.ai/api/search-image?query=old%20carpet%20removed%20bare%20concrete%20subfloor%20empty%20room%20before%20flooring%20installation%20Maryland%20home%20renovation%20neutral%20walls%20ready%20for%20hardwood&width=700&height=480&seq=before-2&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=luxury%20brand%20new%20wide%20plank%20white%20oak%20hardwood%20floor%20installation%20stunning%20elegant%20Maryland%20home%20bright%20natural%20light%20polished%20finish%20premium%20result&width=700&height=480&seq=after-2&orientation=landscape',
    result: 'Custom white oak installation — increased home value by $28K',
  },
  {
    id: 3,
    label: 'Water Damage Repair — Baltimore, MD',
    service: 'Floor Repair',
    serviceSlug: 'repair',
    before: 'https://readdy.ai/api/search-image?query=water%20damaged%20hardwood%20floor%20warped%20buckling%20boards%20dark%20stain%20moisture%20damage%20Maryland%20home%20before%20repair%20ugly%20damaged%20floor&width=700&height=480&seq=before-3&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=perfectly%20repaired%20seamless%20hardwood%20floor%20beautiful%20matching%20grain%20stain%20flawless%20repair%20invisible%20fix%20restored%20wood%20floor%20Maryland%20home%20elegant&width=700&height=480&seq=after-3&orientation=landscape',
    result: 'Seamless repair — invisible to the eye after color matching',
  },
];

interface BeforeAfterProps {
  onEstimateClick: () => void;
}

export default function BeforeAfter({ onEstimateClick }: BeforeAfterProps) {
  return (
    <section className="bg-white py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Real Results</span>
          <h2 className="font-serif text-4xl md:text-5xl text-walnut font-bold mt-3 leading-tight">
            Before &amp; After Gallery
          </h2>
          <p className="font-sans text-charcoal/60 mt-5 text-base max-w-xl mx-auto">
            See the dramatic transformations our craftsmen deliver for Maryland homeowners — real projects, real results.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
          {projects.map((project) => (
            <div key={project.id} className="group rounded-2xl overflow-hidden border border-sand-dark hover:border-oak/30 transition-all duration-300">
              {/* Images */}
              <div className="grid grid-cols-2 h-56 relative">
                <div className="relative overflow-hidden">
                  <img
                    src={project.before}
                    alt={`Before ${project.label}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-charcoal/20" />
                  <div className="absolute bottom-3 left-3">
                    <span className="bg-charcoal/80 text-white font-sans text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
                      Before
                    </span>
                  </div>
                </div>
                <div className="relative overflow-hidden">
                  <img
                    src={project.after}
                    alt={`After ${project.label}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-3 right-3">
                    <span className="bg-gold text-walnut font-sans text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
                      After
                    </span>
                  </div>
                </div>
                {/* Divider */}
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-white z-10" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center z-20">
                  <i className="ri-arrow-left-right-line text-walnut text-sm" />
                </div>
              </div>

              {/* Content */}
              <div className="bg-sand p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-sans text-base font-semibold text-walnut leading-snug">{project.label}</h3>
                  <Link
                    to={`/services/${project.serviceSlug}`}
                    className="text-xs font-sans text-oak font-medium whitespace-nowrap hover:text-walnut transition-colors"
                  >
                    {project.service} →
                  </Link>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-gold/20 rounded flex-shrink-0">
                    <i className="ri-star-fill text-gold text-xs" />
                  </div>
                  <p className="font-sans text-sm text-charcoal/70 leading-relaxed">{project.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Strip */}
        <div className="bg-gradient-to-r from-walnut to-walnut/90 rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl text-white font-bold">Ready to See Your Transformation?</h3>
            <p className="text-white/65 font-sans text-sm mt-2">Every project starts with a free, no-obligation in-home estimate.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={onEstimateClick}
              className="px-8 py-3.5 bg-gold text-walnut font-semibold rounded-lg hover:bg-gold-dark transition-colors font-sans text-sm whitespace-nowrap"
            >
              Get My Free Estimate
            </button>
            <a
              href="tel:+14105550192"
              className="px-8 py-3.5 border-2 border-white/30 text-white font-medium rounded-lg hover:bg-white/10 transition-colors font-sans text-sm whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line" /> Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
